'use client';

import type { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /**
   * Keep this column's cells on a single line. Non-wrapping cells size to their
   * content, so a column claims the width it needs (from the flexible columns'
   * slack) instead of wrapping into a taller row. Use on single-value columns;
   * leave off the one free-flowing column that should absorb the leftover width.
   */
  noWrap?: boolean;
  /**
   * This column's cells hold their own interactive controls (checkboxes,
   * menus). With `rowHref`, such cells are left outside the row link so the
   * control keeps working without also navigating.
   */
  interactive?: boolean;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /**
   * Navigation rows: render each cell's content inside a real link to this
   * URL, so plain click navigates in-app while middle-click, Cmd/Ctrl-click
   * and right-click → "Open in new tab" all work. Use instead of `onRowClick`
   * when a row click is a navigation.
   */
  rowHref?: (row: T) => string | undefined;
  sort?: SortState | null;
  onSort?: (sort: SortState) => void;
  loading?: boolean;
  /** One short line shown when there are no rows. */
  empty?: string;
  /** Pagination / summary row pinned under the table. */
  footer?: ReactNode;
  stickyHeader?: boolean;
  rowClassName?: (row: T) => string | undefined;
  /** Classes for the bordered, horizontally scrolling table wrapper. */
  className?: string;
  /** Classes for the `mobileCard` list wrapper, which is a different box to the table's. */
  mobileCardClassName?: string;
  /**
   * Minimum table width below `md` (default 40rem), so columns keep their
   * shape and the wrapper scrolls sideways instead of every cell wrapping into
   * a sliver. Desktop keeps the auto layout. Pass '0' to let a small table shrink.
   */
  mobileMinWidth?: string;
  /**
   * Below `md`, render rows as a stacked list of cards instead of the table.
   * `'auto'` derives one from the columns (first column as the card title, the
   * rest as label/value lines, `interactive` columns as a trailing action row
   * outside the row link); a function renders each card itself. Row links/
   * clicks still apply to the card, so with `rowHref` a custom card must not
   * contain links or controls of its own — it renders inside the anchor.
   */
  mobileCard?: 'auto' | ((row: T) => ReactNode);
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

function cellContent<T>(col: Column<T>, row: T): ReactNode {
  if (col.render) return col.render(row);
  const value = (row as Record<string, unknown>)[col.key];
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

function AutoCard<T>({ columns, row }: { columns: Column<T>[]; row: T }) {
  const visible = columns.filter((col) => !col.interactive);
  const first = visible.at(0);
  const rest = visible.slice(1);
  return (
    <div className="grid gap-1">
      {first ? <div className="text-sm font-medium text-zinc-50">{cellContent(first, row)}</div> : null}
      {rest.map((col) => (
        <div key={col.key} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="shrink-0 text-zinc-400">{col.header}</span>
          <span className="min-w-0 text-right text-zinc-100">{cellContent(col, row)}</span>
        </div>
      ))}
    </div>
  );
}

function MobileCards<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowHref,
  loading,
  empty,
  mobileCard,
}: Pick<TableProps<T>, 'columns' | 'rows' | 'rowKey' | 'onRowClick' | 'rowHref' | 'loading' | 'empty'> & {
  mobileCard: NonNullable<TableProps<T>['mobileCard']>;
}) {
  const card = 'corner-brand block border border-zinc-800 bg-zinc-900 p-3';
  if (loading) {
    return (
      <div className="grid gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={card}>
            <div className="corner-brand-xs h-3 w-3/5 animate-pulse bg-white/5" />
          </div>
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className={cn(card, 'py-8 text-center text-zinc-500')}>{empty}</div>;
  }
  // Same rule as the table's cells: interactive columns (checkboxes, row menus)
  // stay outside the row link so their controls keep working without navigating.
  const interactive = mobileCard === 'auto' ? columns.filter((col) => col.interactive) : [];
  return (
    <div className="grid gap-2">
      {rows.map((row) => {
        const content = mobileCard === 'auto' ? <AutoCard columns={columns} row={row} /> : mobileCard(row);
        const actions = interactive.length ? (
          <div className="mt-2 flex items-center justify-end gap-2" onClick={(event) => event.stopPropagation()}>
            {interactive.map((col) => (
              <span key={col.key}>{cellContent(col, row)}</span>
            ))}
          </div>
        ) : null;
        const href = rowHref?.(row);
        if (href) {
          return (
            <div key={rowKey(row)} className={cn(card, 'hover:bg-white/[0.03]')}>
              <Link href={href} className="block">
                {content}
              </Link>
              {actions}
            </div>
          );
        }
        return (
          <div
            key={rowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(card, onRowClick && 'cursor-pointer hover:bg-white/[0.03]')}
          >
            {content}
            {actions}
          </div>
        );
      })}
    </div>
  );
}

/** The one table. Plain readable headers, ~40px rows, optional sort + row click. */
export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowHref,
  sort,
  onSort,
  loading = false,
  empty = 'Nothing here.',
  footer,
  stickyHeader = false,
  rowClassName,
  className,
  mobileCardClassName,
  mobileMinWidth = '40rem',
  mobileCard,
}: TableProps<T>) {
  const handleSort = (col: Column<T>) => {
    if (!col.sortable || !onSort) return;
    const dir = sort?.key === col.key && sort.dir === 'desc' ? 'asc' : 'desc';
    onSort({ key: col.key, dir });
  };

  return (
    <>
    {mobileCard ? (
      <div className={cn('md:hidden', mobileCardClassName)}>
        <MobileCards
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          onRowClick={onRowClick}
          rowHref={rowHref}
          loading={loading}
          empty={empty}
          mobileCard={mobileCard}
        />
        {footer ? <div className="flex items-center gap-2 px-1 py-2 text-xs text-zinc-400">{footer}</div> : null}
      </div>
    ) : null}
    <div className={cn('corner-brand wide-scroll overflow-x-auto border border-zinc-800 bg-zinc-900', mobileCard && 'max-md:hidden', className)}>
      <table
        className="w-full text-sm max-md:min-w-(--table-mobile-min-w)"
        style={{ '--table-mobile-min-w': mobileMinWidth } as CSSProperties}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={cn(
                  'px-3 py-2 text-xs font-medium whitespace-nowrap text-zinc-400',
                  ALIGN[col.align ?? 'left'],
                  stickyHeader && 'sticky top-0 z-10 bg-zinc-900'
                )}
              >
                {col.sortable && onSort ? (
                  <button
                    type="button"
                    onClick={() => handleSort(col)}
                    className={cn(
                      'inline-flex items-center gap-1 transition-colors hover:text-zinc-200',
                      sort?.key === col.key && 'text-accent-bright hover:text-accent-bright'
                    )}
                  >
                    {col.header}
                    {sort?.key === col.key ? (
                      sort.dir === 'asc' ? (
                        <ArrowUp className="size-3" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3" aria-hidden />
                      )
                    ) : null}
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }, (_, i) => (
              <tr key={i} className="border-t border-zinc-800/70">
                {columns.map((col) => (
                  <td key={col.key} className="px-3 py-2.5">
                    <div className="corner-brand-xs h-3 w-4/5 animate-pulse bg-white/5" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr className="border-t border-zinc-800/70">
              <td colSpan={columns.length} className="px-3 py-10 text-center text-zinc-500">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const href = rowHref?.(row);
              // Only the row's first linked cell is a tab stop — keyboard users
              // get one stop per row, not one per column.
              let linkedCellSeen = false;
              return (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-t border-zinc-800/70 transition-colors',
                    (onRowClick !== undefined || href) && 'cursor-pointer hover:bg-white/[0.03]',
                    rowClassName?.(row)
                  )}
                >
                  {columns.map((col) => {
                    const content = cellContent(col, row);
                    if (href && !col.interactive) {
                      const tabIndex = linkedCellSeen ? -1 : undefined;
                      linkedCellSeen = true;
                      return (
                        <td key={col.key} className={cn('p-0 align-middle', ALIGN[col.align ?? 'left'])}>
                          <Link
                            href={href}
                            tabIndex={tabIndex}
                            className={cn('block px-3 py-2.5', col.noWrap && 'whitespace-nowrap')}
                          >
                            {content}
                          </Link>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={col.key}
                        className={cn(
                          'px-3 py-2.5 align-middle',
                          col.noWrap && 'whitespace-nowrap',
                          ALIGN[col.align ?? 'left']
                        )}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {footer ? (
        <div className="flex items-center gap-2 border-t border-zinc-800 px-3 py-2 text-xs text-zinc-400">
          {footer}
        </div>
      ) : null}
    </div>
    </>
  );
}
